import useGetProjectByIdOverview from '@/api/queries/project/useGetProjectByIdOverview';
import { usePermissions } from '@/hooks/usePermissions';
import DuplicateProposalModal from '@/ui/pages/proposalBuilderPage/components/proposalLifecycleActions/DuplicateProposalModal';
import { useHashFragment } from '@/utils/customHooks';
import { JobPermissions } from '@payaca/permissions/jobs/job.permissions';
import { ReactNode } from 'react';
import { useHistory } from 'react-router-dom';

// The duplicate flow takes an input (the Customer), so it keeps browser
// history; see docs/technologies/react-webapps.md.
const DUPLICATE_MODAL_HASH = '#duplicate';

/**
 * Duplicating the Proposal onto a new Project, and the modal that takes the
 * Customer for it. Both the Draft's actions menu and the sent Proposal's
 * actions list offer this, so the wiring lives here rather than in each.
 *
 * Render `modal` — an Ambrosia Modal has to stay mounted for its close
 * animation to finish.
 */
export const useDuplicateProposal = (params: {
  proposalId: string;
  projectId: string;
}): { canDuplicate: boolean; openDuplicate: () => void; modal: ReactNode } => {
  const { proposalId, projectId } = params;

  const history = useHistory();
  const { project } = useGetProjectByIdOverview(projectId);
  const { userHasRequiredPermission } = usePermissions();

  const [isOpen, toggleModal] = useHashFragment(DUPLICATE_MODAL_HASH);

  return {
    canDuplicate: userHasRequiredPermission([JobPermissions.DUPLICATE_JOB]),
    openDuplicate: toggleModal,
    modal: (
      <DuplicateProposalModal
        isOpen={isOpen}
        onClose={toggleModal}
        proposalId={proposalId}
        defaultCustomer={
          project?.customer
            ? { id: Number(project.customer.id), name: project.customer.name }
            : undefined
        }
        onDuplicated={(copy) =>
          history.push(
            `/projects/${copy.projectId}/proposals/${copy.proposalId}`
          )
        }
      />
    ),
  };
};
